import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton, Tooltip } from "@mui/material";
import {
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridAggregationModel,
  GridColDef,
} from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import { NumberFormatter } from "hooks/serialisers/numbers";
import { TFunction } from "i18next";
import { CacheLine } from "src/store/api/generatedApi";

dayjs.extend(duration);
dayjs.extend(relativeTime);

interface BuildCacheColumnsArgs {
  format: NumberFormatter;
  toLocalDatetime: (isoString: string) => string;
  invalidateKeys: (keys: string[]) => Promise<unknown>;
  t: TFunction;
}

export const buildCacheColumns = ({
  format,
  toLocalDatetime,
  invalidateKeys,
  t,
}: BuildCacheColumnsArgs): GridColDef<CacheLine>[] => {
  return [
    {
      field: "key",
      align: "right",
    },
    {
      field: "original_id",
      sortable: true,
    },
    {
      field: "category",
      sortable: true,
    },
    {
      field: "type_name",
      sortable: true,
    },
    {
      field: "expires_at",
      headerName: t("expiresAt"),
      sortable: true,
      valueFormatter: (value: string | null) =>
        value ? toLocalDatetime(value) : "",
      width: 125,
    },
    {
      field: "ttl_seconds",
      headerName: t("timeLeft"),
      sortable: true,
      valueFormatter: (value: number | null) =>
        value
          ? dayjs.duration({ seconds: Math.floor(value) }).humanize()
          : null,
    },
    {
      field: "size_in_bytes",
      headerName: t("size"),
      sortable: true,
      type: "number",
      aggregable: true,
      headerAlign: "right",
      align: "right",
      valueFormatter: humanReadableSize(format),
    },
    {
      field: "_invalidate",
      headerName: t("invalidate"),
      headerAlign: "center",
      align: "center",
      renderCell: ({ row, rowNode, id }) => {
        const isGroupRow = id.toString().startsWith("auto-generated-row");
        const keys = isGroupRow
          ? "children" in rowNode
            ? rowNode.children.map((id) => id.toString())
            : []
          : [row.key];
        const areChildrenOnlyGroupRows =
          "children" in rowNode
            ? rowNode.children.every((child) =>
                child.toString().startsWith("auto-generated-row")
              )
            : false;
        if (areChildrenOnlyGroupRows) return null;
        if (keys.length === 0) return null;
        return (
          <Tooltip title={t("invalidate")} sx={{ mx: "auto" }}>
            <IconButton
              onClick={() => void invalidateKeys(keys)}
              color="primary"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "redis_type",
      headerName: t("redisType"),
      sortable: true,
    },
    {
      field: "encoding",
      headerName: t("encoding"),
      sortable: true,
    },
  ];
};

const humanReadableSize =
  (format: NumberFormatter) =>
  (bytes: number | null | undefined): string | null => {
    if (bytes === null || bytes === undefined) return null;
    const wholeBytes = Math.floor(bytes);
    // These are 1000s and not 1024s as we display KB and not KiB (kibibytes)
    if (wholeBytes < 1e3)
      return `${format(wholeBytes, { decimalPlaces: 1 })} B`;
    if (wholeBytes < 1e6)
      return `${format(wholeBytes / 1e3, { decimalPlaces: 1 })} KB`;
    if (wholeBytes < 1e9)
      return `${format(wholeBytes / 1e6, { decimalPlaces: 1 })} MB`;
    return `${format(wholeBytes / 1e9, { decimalPlaces: 1 })} GB`;
  };

export const cacheColumnsAggregationModel: GridAggregationModel = {
  size_in_bytes: "sum",
  redis_type: "allSame",
  encoding: "allSame",
};

export const allSameAggregation: GridAggregationFunction<
  string | null,
  string | null
> = {
  label: "",
  apply: ({ values }) => {
    const filtered = values.filter(
      (v): v is string => v !== null && v !== undefined
    );
    if (filtered.length === 0) return null;
    const first = filtered[0];
    return filtered.every((v) => v === first) ? first : "...";
  },
  columnTypes: ["string"],
};

export const cacheColumnsAggregationFunctions = {
  ...GRID_AGGREGATION_FUNCTIONS,
  sum: { ...GRID_AGGREGATION_FUNCTIONS.sum, label: "" },
  allSame: allSameAggregation,
} as const;
